// El calendario de un lanzamiento de Instagram —
// ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts.
//
// Se comporta como la tabla de productos: solo ocupa todo, y al abrir un post se
// achica a una columna angosta con el editor al lado, interpolando
// `grid-template-columns` igual que aquella
// (ADR-2026-09-15-el-post-se-edita-al-lado-del-calendario). Las dos formas del
// lienzo viven en `../pantalla`, para que las dos pantallas se achiquen igual.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Play, Plus } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import { PANTALLA_DETALLE, PANTALLA_LISTA } from '../pantalla';
import {
  DIAS_DE_LA_SEMANA,
  correrMes,
  grillaDelMes,
  horaDe,
  mesDe,
  tituloDelMes,
  type DiaDelMes,
} from './calendario';
import { sugerirMomento, type PostEnAgenda } from './mejorMomento';
import { EditorDePost } from './EditorDePost';
import { problemasDelPost } from './post';
import {
  crearPost,
  fetchAgendaDeLaEmpresa,
  fetchPosts,
  type PostConMiniaturas,
} from './postsApi';

/** Cuántos posts se ven en una casilla antes de resumir el resto. */
const POSTS_VISIBLES = 2;

export function CalendarioDeRedes({
  lanzamientoId,
  companyId,
  fechaLanzamiento,
  aceptaVideo,
  onCambio,
}: {
  lanzamientoId: string;
  companyId: string;
  /** `YYYY-MM-DD` de la fecha del lanzamiento, si tiene. */
  fechaLanzamiento: string | null;
  aceptaVideo: boolean;
  /** Para que el encabezado del detalle recuente. */
  onCambio: () => void;
}) {
  const [mes, setMes] = useState(() => mesDe(new Date()));
  const [posts, setPosts] = useState<PostConMiniaturas[]>([]);
  const [agenda, setAgenda] = useState<PostEnAgenda[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  /**
   * `onCambio` vive en una ref y NO en las dependencias de nada: el detalle lo
   * pasa nuevo en cada pintado y, si entrara, la lectura se dispararía a sí
   * misma para siempre (ADR-2026-09-15-el-calendario-se-recargaba-a-si-mismo).
   */
  const avisarRef = useRef(onCambio);
  avisarRef.current = onCambio;

  const desde = useMemo(() => {
    if (!fechaLanzamiento) return null;
    const [a, m, d] = fechaLanzamiento.split('-').map(Number);
    return a && m && d ? new Date(a, m - 1, d) : null;
  }, [fechaLanzamiento]);

  /** Leer. No avisa nada hacia arriba: leer no es un cambio. */
  const recargar = useCallback(async () => {
    const [p, a] = await Promise.all([
      fetchPosts(lanzamientoId, companyId),
      fetchAgendaDeLaEmpresa(companyId),
    ]);
    setPosts(p.posts);
    setAgenda(a.agenda);
    setAviso(p.error ?? a.error);
    setCargando(false);
  }, [lanzamientoId, companyId]);

  /** Leer y avisar. Va después de ESCRIBIR, que es cuando los contadores se mueven. */
  const recargarYAvisar = useCallback(async () => {
    await recargar();
    avisarRef.current();
  }, [recargar]);

  useEffect(() => {
    void recargar();
  }, [lanzamientoId, companyId, recargar]);

  const grilla = useMemo(() => grillaDelMes(mes, posts, new Date()), [mes, posts]);
  const abiertoEl = posts.find((p) => p.id === abierto) ?? null;
  const angosto = !!abiertoEl;

  /** Tocar un día vacío: el día lo eligió el usuario, la hora la elegimos nosotros. */
  const nuevoEn = async (dia: Date | null) => {
    const acotado = dia
      ? {
          desde: dia,
          hasta: new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), 23, 59),
        }
      : { desde };
    const s = sugerirMomento({ agenda, ahora: new Date(), ...acotado });
    const r = await crearPost(lanzamientoId, companyId, s.cuando);
    if (r.error || !r.id) return setAviso(r.error);
    await recargarYAvisar();
    setAbierto(r.id);
  };

  if (cargando) {
    return (
      <div className="mt-8 flex items-center justify-center gap-2 py-16 text-sm text-neutral-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando el calendario…
      </div>
    );
  }

  return (
    <div className="mt-4">
      {aviso && (
        <p className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 py-3 px-4 text-sm text-brand-black">
          {aviso}
        </p>
      )}

      <div
        className="transicion-tabla mt-4 md:grid md:items-start"
        style={{
          gridTemplateColumns: angosto ? PANTALLA_DETALLE : PANTALLA_LISTA,
          columnGap: angosto ? '1.5rem' : '0px',
        }}
      >
        {/* El calendario. Al abrir un post NO se cambia por otra cosa: es el
            mismo mes, que se achica y se queda de columna lateral. En mobile no
            hay lugar para las dos, así que se esconde. */}
        <div className={angosto ? 'hidden md:block' : ''}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1">
              <h2
                className={`transicion-achique truncate font-semibold capitalize text-brand-black ${
                  angosto ? 'text-sm' : 'text-lg'
                }`}
              >
                {tituloDelMes(mes)}
              </h2>
              <button
                type="button"
                onClick={() => setMes((m) => correrMes(m, -1))}
                aria-label="Mes anterior"
                className="ml-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setMes((m) => correrMes(m, 1))}
                aria-label="Mes siguiente"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {!angosto && (
                <button
                  type="button"
                  onClick={() => setMes(mesDe(new Date()))}
                  className="ml-1 rounded-lg py-1.5 px-3 text-sm text-neutral-500 hover:bg-neutral-100"
                >
                  Hoy
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => void nuevoEn(null)}
              aria-label="Nuevo post"
              className="flex shrink-0 items-center gap-1.5 rounded-xl bg-brand-black py-2.5 px-5 text-sm font-medium text-white"
            >
              <Plus className="w-4 h-4" />
              {!angosto && 'Nuevo post'}
            </button>
          </div>

          <div className="mt-3 overflow-hidden rounded-2xl border border-neutral-200">
            <div className="grid grid-cols-7 border-b border-neutral-200 bg-neutral-50">
              {DIAS_DE_LA_SEMANA.map((d) => (
                <div
                  key={d}
                  className="py-2 px-1 text-center text-[11px] font-medium text-neutral-500"
                >
                  {angosto ? d[0] : d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {grilla.map((dia) => (
                <Casilla
                  key={dia.fecha.toISOString()}
                  dia={dia}
                  angosta={angosto}
                  abierto={abierto}
                  onNuevo={() => void nuevoEn(dia.fecha)}
                  onAbrir={setAbierto}
                />
              ))}
            </div>
          </div>
        </div>

        {/* El post abierto. La `key` en el envoltorio y no en el editor para que
            la animación de entrada vuelva a correr al cambiar de post. */}
        <div className="min-w-0">
          {abiertoEl && (
            <div key={abiertoEl.id} className="animate-ficha-entra">
              <EditorDePost
                post={abiertoEl}
                companyId={companyId}
                agenda={agenda}
                desde={desde}
                aceptaVideo={aceptaVideo}
                onCambio={() => void recargarYAvisar()}
                onCerrar={() => setAbierto(null)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Casilla({
  dia,
  angosta,
  abierto,
  onNuevo,
  onAbrir,
}: {
  dia: DiaDelMes<PostConMiniaturas>;
  angosta: boolean;
  /** El post abierto, para marcarlo en la grilla achicada. */
  abierto: string | null;
  onNuevo: () => void;
  onAbrir: (id: string) => void;
}) {
  /**
   * Un día con muchos posts se despliega en el lugar.
   *
   * Antes "+N más" abría `dia.posts[POSTS_VISIBLES]`, un índice FIJO: con
   * cuatro o más en el mismo día, del cuarto en adelante no había forma de
   * llegar —ni para editarlo ni para borrarlo— sin antes mover los de arriba.
   * Desplegar da un botón por post y no inventa una pantalla nueva
   * (ADR-2026-09-16-un-dia-cargado-se-despliega-en-el-lugar).
   */
  const [desplegado, setDesplegado] = useState(false);

  // Al volver a la grilla ancha o al cambiar de mes se repliega solo: dejarlo
  // abierto desacomoda la fila entera del calendario.
  useEffect(() => {
    setDesplegado(false);
  }, [angosta, dia.fecha.getMonth()]);

  const visibles = desplegado ? dia.posts : dia.posts.slice(0, POSTS_VISIBLES);
  const resto = dia.posts.length - visibles.length;
  const hayAbierto = dia.posts.some((p) => p.id === abierto);

  const numero = (
    <span
      className={`transicion-achique grid place-items-center rounded-full ${
        angosta ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-[11px]'
      } ${
        dia.esHoy
          ? 'bg-brand-black font-medium text-white'
          : dia.delMes
            ? 'text-brand-black'
            : 'text-neutral-400'
      }`}
    >
      {dia.fecha.getDate()}
    </span>
  );

  // UN solo elemento que cambia de tamaño, no dos vistas que se turnan: si la
  // casilla angosta fuera otro elemento, React desmontaría y remontaría y no
  // habría nada que interpolar — el alto saltaría mientras el ancho se mueve
  // suave (ADR-2026-09-15-el-achique-tambien-se-anima-a-lo-alto).
  return (
    <div
      className={`transicion-achique group relative border-b border-r border-neutral-200 ${
        angosta ? 'min-h-[42px] p-1' : 'min-h-[104px] p-1.5'
      } ${dia.delMes ? 'bg-white' : 'bg-neutral-50'} ${
        angosta && hayAbierto ? 'ring-1 ring-inset ring-brand-primary' : ''
      }`}
    >
      {angosta ? (
        // Achicada la casilla es un día con puntitos: a 37px de ancho no entra
        // ni la hora. Misma idea que las columnas de la tabla colapsando a cero.
        <button
          type="button"
          disabled={dia.posts.length === 0}
          onClick={() => dia.posts[0] && onAbrir(dia.posts[0].id)}
          className={`flex w-full flex-col items-center gap-0.5 rounded ${
            dia.posts.length > 0 ? 'hover:bg-neutral-100' : ''
          }`}
        >
          {numero}
          <span className="flex gap-0.5">
            {dia.posts.slice(0, 3).map((p) => (
              <span key={p.id} className="h-1 w-1 rounded-full bg-brand-primary" />
            ))}
          </span>
        </button>
      ) : (
        <>
          <div className="flex items-center justify-between">
            {numero}
            {/* Agregar sólo hacia adelante: un post en el pasado no lo publica nadie. */}
            {!dia.esPasado && (
              <button
                type="button"
                onClick={onNuevo}
                aria-label="Nuevo post este día"
                className="grid h-6 w-6 place-items-center rounded-lg text-neutral-300 opacity-0 hover:bg-neutral-100 hover:text-brand-black focus:opacity-100 group-hover:opacity-100"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="mt-1 space-y-1">
            {visibles.map((p) => {
              const incompleto = problemasDelPost(p).length > 0;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onAbrir(p.id)}
                  className="flex w-full items-center gap-1.5 rounded-lg py-1 px-1 text-left hover:bg-neutral-100"
                >
                  <span className="grid h-6 w-6 shrink-0 place-items-center overflow-hidden rounded bg-neutral-100">
                    {p.miniaturas[0] ? (
                      <CachedImg
                        src={p.miniaturas[0]}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Play className="w-3 h-3 text-neutral-400" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[11px] text-neutral-500">
                    {horaDe(p)}
                  </span>
                  {incompleto && (
                    <span
                      aria-label="Le falta algo"
                      title="Le falta algo"
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400"
                    />
                  )}
                </button>
              );
            })}
            {resto > 0 && (
              <button
                type="button"
                onClick={() => setDesplegado(true)}
                className="w-full rounded-lg py-0.5 px-1 text-left text-[11px] text-neutral-500 hover:bg-neutral-100"
              >
                +{resto} más
              </button>
            )}
            {desplegado && dia.posts.length > POSTS_VISIBLES && (
              <button
                type="button"
                onClick={() => setDesplegado(false)}
                className="w-full rounded-lg py-0.5 px-1 text-left text-[11px] text-neutral-500 hover:bg-neutral-100"
              >
                Ver menos
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
